import { l as DocContext, o as Context, k as jsxRuntimeExports, t as textRendererStyle } from "./doenetml-drNYjJiF.js";
import React__default, { useRef, useContext } from "react";
import { j as jxg } from "./JSXGraphRenderer-BcR36ozd.js";
import { BoardContext, TEXT_LAYER_OFFSET } from "./graph-uNmxqd5I.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import { g as getPositionFromAnchorByCoordinate } from "./graph-W6vrFcBu.js";
import { ChoiceInputInlineContext } from "./choiceInput-QYG5RDnq.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { d as detachAnchoredGraphElement, a as attachAnchoredGraphDragHandlers } from "./useAnchoredGraphDragHandler-BEy2dmsI.js";
const text = React__default.memo(function Text(props) {
  let { componentIdx, id, SVs, actions, callAction } = useDoenetRenderer(props);
  Text.ignoreActionsWithoutCore = () => true;
  const textJXG = useRef(null);
  const anchorPointJXG = useRef(null);
  const anchorRel = useRef(null);
  const board = useContext(BoardContext);
  const choiceInputInlineContext = useContext(ChoiceInputInlineContext);
  const pointerState = usePointerDragState();
  const pointAtDown = useRef(null);
  const calculatedX = useRef(null);
  const calculatedY = useRef(null);
  const previousPositionFromAnchor = useRef(null);
  const { fixed, fixLocation, lastPositionFromCore } = useDraggableRefs(SVs, null);
  useBoardPointerTracking(board, pointerState);
  useJSXGraphCleanup({
    objectRef: textJXG,
    destroy: () => detachAnchoredGraphElement(textJXG, board)
  });
  const { darkMode } = useContext(DocContext) || {};
  function createTextJXG() {
    if (board === null) {
      return null;
    }
    let textColor = darkMode === "dark" ? SVs.selectedStyle.textColorDarkMode : SVs.selectedStyle.textColor;
    let backgroundColor = darkMode === "dark" ? SVs.selectedStyle.backgroundColorDarkMode : SVs.selectedStyle.backgroundColor;
    let cssStyle = ``;
    if (backgroundColor) {
      cssStyle += `background-color: ${backgroundColor}`;
    }
    let jsxTextAttributes = {
      visible: !SVs.hidden,
      fixed: fixed.current,
      layer: 10 * SVs.layer + TEXT_LAYER_OFFSET,
      cssStyle,
      highlightCssStyle: cssStyle,
      strokeColor: textColor,
      strokeOpacity: 1,
      highlightStrokeColor: textColor,
      highlightStrokeOpacity: 0.5,
      highlight: !fixLocation.current,
      parse: false
    };
    let newAnchorPointJXG;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      let anchorCoords = [
        anchor.get_component(0).evaluate_to_constant() ?? NaN,
        anchor.get_component(1).evaluate_to_constant() ?? NaN
      ];
      if (!Number.isFinite(anchorCoords[0])) {
        anchorCoords[0] = 0;
        jsxTextAttributes["visible"] = false;
      }
      if (!Number.isFinite(anchorCoords[1])) {
        anchorCoords[1] = 0;
        jsxTextAttributes["visible"] = false;
      }
      newAnchorPointJXG = board.create("point", anchorCoords, {
        visible: false
      });
    } catch (e) {
      jsxTextAttributes["visible"] = false;
      newAnchorPointJXG = board.create("point", [0, 0], {
        visible: false
      });
    }
    jsxTextAttributes.anchor = newAnchorPointJXG;
    let { anchorx, anchory } = getPositionFromAnchorByCoordinate(
      SVs.positionFromAnchor
    );
    jsxTextAttributes.anchorx = anchorx;
    jsxTextAttributes.anchory = anchory;
    anchorRel.current = [anchorx, anchory];
    let newTextJXG = board.create(
      "text",
      [0, 0, SVs.text],
      jsxTextAttributes
    );
    attachAnchoredGraphDragHandlers({
      board,
      newJXG: newTextJXG,
      newAnchorPoint: newAnchorPointJXG,
      anchorRel,
      pointerState,
      pointAtDown,
      calculatedX,
      calculatedY,
      fixed,
      fixLocation,
      lastPositionFromCore,
      componentIdx,
      actions,
      callAction,
      actionNames: {
        move: "moveText",
        focused: "textFocused",
        clicked: "textClicked"
      }
    });
    textJXG.current = newTextJXG;
    anchorPointJXG.current = newAnchorPointJXG;
    previousPositionFromAnchor.current = SVs.positionFromAnchor;
  }
  if (board) {
    let anchorCoords;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      anchorCoords = [
        anchor.get_component(0).evaluate_to_constant() ?? NaN,
        anchor.get_component(1).evaluate_to_constant() ?? NaN
      ];
    } catch (e) {
      anchorCoords = [NaN, NaN];
    }
    lastPositionFromCore.current = anchorCoords;
    if (textJXG.current === null) {
      createTextJXG();
    } else {
      textJXG.current.relativeCoords.setCoordinates(
        jxg.COORDS_BY_USER,
        [0, 0]
      );
      anchorPointJXG.current?.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        anchorCoords
      );
      textJXG.current.setText(SVs.text);
      let visible = !SVs.hidden;
      if (Number.isFinite(anchorCoords[0]) && Number.isFinite(anchorCoords[1])) {
        let actuallyChangedVisibility = textJXG.current.visProp["visible"] !== visible;
        textJXG.current.visProp["visible"] = visible;
        textJXG.current.visPropCalc["visible"] = visible;
        if (actuallyChangedVisibility) {
          textJXG.current.setAttribute({ visible });
        }
      } else {
        textJXG.current.visProp["visible"] = false;
        textJXG.current.visPropCalc["visible"] = false;
      }
      let layer = 10 * SVs.layer + TEXT_LAYER_OFFSET;
      let layerChanged = textJXG.current.visProp.layer !== layer;
      if (layerChanged) {
        textJXG.current.setAttribute({ layer });
      }
      let textColor = darkMode === "dark" ? SVs.selectedStyle.textColorDarkMode : SVs.selectedStyle.textColor;
      let backgroundColor = darkMode === "dark" ? SVs.selectedStyle.backgroundColorDarkMode : SVs.selectedStyle.backgroundColor;
      let cssStyle = ``;
      if (backgroundColor) {
        cssStyle += `background-color: ${backgroundColor}`;
      } else {
        cssStyle += `background-color: transparent`;
      }
      if (textJXG.current.visProp.strokecolor !== textColor) {
        textJXG.current.visProp.strokecolor = textColor;
        textJXG.current.visProp.highlightstrokecolor = textColor;
      }
      if (textJXG.current.visProp.cssstyle !== cssStyle) {
        textJXG.current.visProp.cssstyle = cssStyle;
        textJXG.current.visProp.highlightcssstyle = cssStyle;
      }
      textJXG.current.visProp.highlight = !fixLocation.current;
      textJXG.current.visProp.fixed = fixed.current;
      textJXG.current.isDraggable = !fixLocation.current;
      textJXG.current.needsUpdate = true;
      if (SVs.positionFromAnchor !== previousPositionFromAnchor.current) {
        let { anchorx, anchory } = getPositionFromAnchorByCoordinate(
          SVs.positionFromAnchor
        );
        textJXG.current.visProp.anchorx = anchorx;
        textJXG.current.visProp.anchory = anchory;
        anchorRel.current = [anchorx, anchory];
        previousPositionFromAnchor.current = SVs.positionFromAnchor;
        textJXG.current.fullUpdate();
      } else {
        textJXG.current.update();
      }
      if (anchorPointJXG.current) {
        anchorPointJXG.current.needsUpdate = true;
        anchorPointJXG.current.update();
      }
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id });
  }
  if (SVs.hidden) {
    return null;
  }
  const style = !choiceInputInlineContext.inOption ? textRendererStyle(darkMode ?? "light", SVs.selectedStyle) : void 0;
  return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id, style, children: SVs.text }) });
});
export {
  text as default
};
